/**
 * Apollo error link that recovers an expired in-memory access token by
 * rotating it from the HttpOnly refresh cookie and replaying the failed
 * operation (WONE-983).
 *
 * An expired/absent bearer surfaces differently per surface:
 *
 *   - REST surfaces (e.g. /reports/api) return HTTP 401.
 *   - GraphQL returns HTTP **200** with the failure in the ``errors``
 *     array. graphene never 401s a resolver failure: Platform's OAuth
 *     middleware leaves an expired-token request anonymous, and the
 *     resolver's ``@login_required`` then raises ``PermissionDenied``.
 *
 * The learner web app talks to GraphQL, so before WONE-983 the
 * HTTP-401-only check never fired and an expired session fell through to
 * the page's generic "Something went wrong" error. Both surfaces now
 * recover the same way: refresh, then replay once.
 *
 * ``onError`` invokes this handler only on the first failure — the
 * replayed ``forward(operation)`` pipes straight to the subscriber and
 * is not passed back through the handler — so a still-failing replay
 * surfaces to the caller without looping. When the refresh cookie is
 * also gone, ``refresh`` itself drives the re-auth (alert + reload).
 */
import { onError } from "apollo-link-error";
import { Observable } from "apollo-link";
import type { FetchResult, NextLink, Operation } from "apollo-link";
import type { GraphQLError } from "graphql";

/**
 * Messages Platform emits for an unauthenticated GraphQL request. There
 * is no machine-readable ``extensions.code`` to key on, so we match the
 * exact strings — and the strings differ across the OAuth-hardening
 * migration (WONE-552), so we match every variant a live environment can
 * still return:
 *
 *   - "Authentication required" — the strict-mode (RS256-only) message,
 *     raised once the legacy HS256/tokenAuth path is removed. This is the
 *     current staging shape and where the WONE-983 fix regressed: the
 *     flip changed the wording out from under the original match set.
 *   - "You do not have permission to perform this action" — what an
 *     anonymous graphql-jwt ``@login_required`` resolver raises; still
 *     the production shape during the dual-run window.
 *   - "Signature has expired" — the legacy HS256 decode path.
 *   - "User is not authenticated." — the hand-rolled `user.is_anonymous`
 *     check in the fire-and-forget `Report*` tracking mutations
 *     (users/schema.py), which don't go through `scope_required` and so
 *     never got the "Authentication required" wording (WONE-1144).
 *
 * Deliberately NOT matched: "Permission denied" — the strict-mode
 * message for a genuine authorization denial on an *authenticated*
 * session (e.g. a non-admin hitting an admin-only resolver). Refreshing
 * the token cannot fix that, so we let it surface instead of spending a
 * pointless rotation. (The legacy build conflated this with the expiry
 * case under the shared permission string; strict mode splits them,
 * which lets us be precise here.)
 */
const AUTH_EXPIRY_MESSAGES: ReadonlySet<string> = new Set([
  "Authentication required",
  "You do not have permission to perform this action",
  "Signature has expired",
  "User is not authenticated.",
]);

export function isAuthExpiryError(
  graphQLErrors?: ReadonlyArray<Pick<GraphQLError, "message">>
): boolean {
  return (
    !!graphQLErrors &&
    graphQLErrors.some((error) => AUTH_EXPIRY_MESSAGES.has(error.message))
  );
}

/**
 * Bridge a Promise into a zen-observable so the refreshed token resolves
 * before the operation is replayed. See
 * https://github.com/apollographql/apollo-link/issues/646#issuecomment-423279220
 */
function promiseToObservable<T>(promise: Promise<T>): Observable<T> {
  return new Observable<T>((subscriber) => {
    promise.then(
      (value) => {
        if (subscriber.closed) return;
        subscriber.next(value);
        subscriber.complete();
      },
      (err) => subscriber.error(err)
    );
  });
}

type AuthErrorLinkDeps = {
  /**
   * Rotate the in-memory access token from the refresh cookie. Resolves
   * once the token is refreshed (or, on a dead cookie, once re-auth has
   * been triggered) — it must not reject, since the operation is
   * replayed unconditionally afterwards.
   */
  refresh: () => Promise<unknown>;
  /** Force re-authentication when the account itself is disabled. */
  onDisabledUser: () => void;
};

export function createAuthErrorLink({
  refresh,
  onDisabledUser,
}: AuthErrorLinkDeps) {
  return onError(({ graphQLErrors, operation, forward }) => {
    const { response } = operation.getContext();

    if (response?.status === 401 || isAuthExpiryError(graphQLErrors)) {
      return promiseToObservable(refresh()).flatMap(() =>
        (forward as NextLink)(operation as Operation)
      ) as Observable<FetchResult>;
    }

    if (graphQLErrors?.some(({ message }) => message === "User is disabled")) {
      onDisabledUser();
    }
  });
}
